<template>
  <div class="p-4 bg-white rounded-lg">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">Email Purchase Order - {{ purchaseOrderNumber }}</h2>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Email Address</label>
      <input class="bge-input bge-input-spacing rounded w-full" v-model="recipientEmail" type="email"
             placeholder="Enter recipient email address"/>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Subject</label>
      <input class="bge-input bge-input-spacing rounded w-full" v-model="emailSubject" type="text"
      />
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Email Text</label>
      <StyledTextEditor v-model="emailMessage" :options="editorOptions"
      />
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Send Copy To (Sent as separate email)</label>
      <Chips v-model="ccEmail" class="w-full" separator=","/>
    </div>

    <div class="mb-4 flex items-center">
      <Checkbox v-model="hasAttachments" :binary="true" class="mr-2"/>
      <label class="block uppercase font-semibold text-sm">Include 'PO' Attachment</label>
    </div>

    <div class="flex justify-end space-x-2">
      <AppButton color="gray" @click="setAsDefault" label="Reset Text"></AppButton>
      <AppButton color="blue" @click="sendOrder" label="Send Order">
        <template v-slot:icon>
          <Icon iconType="send"/>
        </template>
      </AppButton>
    </div>

    <div class="mt-10">
      <EmailQueueTable ref="emailQueueTable" :referenceId="this.purchaseOrderId"></EmailQueueTable>
    </div>
  </div>
</template>

<script>
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import StyledTextEditor from "@/components/StyledTextEditor.vue";
import notificationMixin from "@/mixins/notificationMixin";
import EmailQueueTable from "@/views/HR/Purchases/Components/EmailQueueTable.vue";

export default {
  name: "EmailSend",
  mixins: [notificationMixin],
  components: {
    Icon,
    AppButton,
    EmailQueueTable,
    StyledTextEditor
  },
  props: {
    purchaseOrderId: {
      type: String,
      required: true,
    },
    purchaseOrderNumber: {
      type: String,
      required: true,
    },
    supplier: {
      type: Object,
      required: true,
    },
    purchaseOrder: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      recipientEmail: '',
      emailSubject: '',
      emailMessage: '',
      ccEmail: [],
      defaultSubject: '',
      defaultEmailText: '',
      hasAttachments: true,
      attachmentUrls: '',
      editorOptions: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{'list': 'ordered'}, {'list': 'bullet'}],
        ]
      },
    };
  },
  mounted() {
    this.fetchEmailTemplate();

    const email =
        this.supplier &&
        this.supplier.people &&
        (this.supplier.people.email || this.supplier.people.primary_email);

    this.recipientEmail = email || '';
  },
  methods: {
    replacePlaceholders(template, data) {
      return template.replace(/{{\s*(\w+)\s*}}/g, (match, p1) => data[p1] || '');
    },
    async fetchEmailTemplate() {
      try {
        const template = await this.EmailService.getTemplateByKey('purchase_order_email_temp');

        this.defaultSubject = this.replacePlaceholders(template.subject || '', {
          orderNumber: this.purchaseOrderNumber
        });

        const supplierFirstName =
            (this.supplier && this.supplier.people && this.supplier.people.firstname)
                ? this.supplier.people.firstname
                : '';

        const supplierLastName =
            (this.supplier && this.supplier.people && this.supplier.people.lastname)
                ? this.supplier.people.lastname
                : '';

        const requestedDate =
            (this.purchaseOrder && this.purchaseOrder.requested_delivery_date)
                ? this.purchaseOrder.requested_delivery_date
                : '';

        this.defaultEmailText = this.replacePlaceholders(template.body || '', {
          supplier: `${supplierFirstName} ${supplierLastName}`.trim(),
          orderNumber: this.purchaseOrderNumber,
          requestedDate: requestedDate
        });

        this.emailSubject = this.defaultSubject;
        this.emailMessage = this.defaultEmailText;

      } catch (error) {
        console.error('Error fetching email template:', error);
      }
    },
    setAsDefault() {
      this.emailSubject = this.defaultSubject;
      this.emailMessage = this.defaultEmailText;
    },
    async sendOrder() {
      const supplierFirstName =
          (this.supplier && this.supplier.people && this.supplier.people.firstname)
              ? this.supplier.people.firstname
              : '';

      const supplierLastName =
          (this.supplier && this.supplier.people && this.supplier.people.lastname)
              ? this.supplier.people.lastname
              : '';

      const requestedDate =
          (this.purchaseOrder && this.purchaseOrder.requested_delivery_date)
              ? this.purchaseOrder.requested_delivery_date
              : '';

      const emailData = {
        template_key: 'purchase_order_email_temp',
        reference_id: this.purchaseOrderId,
        to: [
          {
            name: (`${supplierFirstName} ${supplierLastName}`.trim()) || (this.supplier && this.supplier.name ? this.supplier.name : 'Supplier'),
            email: this.recipientEmail
          }
        ],

        cc: Array.isArray(this.ccEmail) ? this.ccEmail : [],
        bcc: [],
        status: "PENDING",
        category: "notification",
        data: {
          supplier: `${supplierFirstName} ${supplierLastName}`.trim(),
          orderNumber: this.purchaseOrderNumber,
          requestedDate: requestedDate,
        },
        use_custom_subject_body: true,
        custom_subject: this.emailSubject,
        custom_body: this.emailMessage,
        has_attachments: this.hasAttachments,
        attachments: this.hasAttachments ? [
          {
            url: `purchase-orders/${this.purchaseOrderId}/report/purchase-order`,
            file_name: `${this.purchaseOrderNumber}-purchase-order.pdf`
          }
        ] : []
      };

      try {
        await this.EmailService.createQueueItem(emailData);
        this.notifySuccess('Email sent successfully', 'success');
        await this.$refs.emailQueueTable.loadEmailQueues();
      } catch (error) {
        console.error('Error sending email:', error);
        this.notifyError('Failed to send email', 'error');
      }
    }
  },
};
</script>


<style scoped>
/* No additional styles required */
</style>
